import Truncate from 'react-truncate';
import { screen, colors, fonts } from 'components/styles/variables';
import { useMedia } from 'react-use';
import { useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Spacer from 'components/Shared/Spacer';
import useEvents from 'lib/hooks/useEvents';
import toggleEvent from 'lib/Tracking/Vdp/SellerSnapshot/ToggleEvent';
import VdpEventContext from 'contexts/VdpEventContext';
import css from 'styled-jsx/css';
import LinkButton from 'components/Shared/LinkButton';
import SmsVariantShowButton from 'components/Vdp/SmsVariantExpandButton';

const MOBILE_LINES = 5;
const DESKTOP_LINES = 9;

interface Props {
  description: string | null;
  className?: string;
  dealerDisclaimer?: string | null;
  smsFunctionality?: boolean;
}

interface UseTruncated {
  isTruncated: boolean;
  expand(): void;
}

function useTruncated(): UseTruncated {
  const [isExpanded, setIsExpanded] = useState(false);

  function expand(): void {
    setIsExpanded(!isExpanded);
  }

  return { isTruncated: !isExpanded, expand };
}

const linkButtonCss = css.resolve`
  align-self: flex-start;
`;

export default function SellerDescription({
  description,
  className,
  dealerDisclaimer,
  smsFunctionality,
}: Props): JSX.Element {
  const isMobile = useMedia(`(max-width: ${screen.xsMax})`);
  const collapsedLinesToShow = isMobile ? MOBILE_LINES : DESKTOP_LINES;
  const { isTruncated, expand } = useTruncated();
  const [isTextOverTruncateLimit, setIsTextOVerTruncateLimit] = useState(false);
  const { t } = useTranslation();
  const logger = useEvents();
  const { trackingParams } = useContext(VdpEventContext);

  const handleExpandClick = useCallback((): void => {
    logger.log(toggleEvent({ isTruncated, trackingParams }));
    expand();
  }, [logger, isTruncated, trackingParams, expand]);

  return (
    <div className={`description ${className}`}>
      {dealerDisclaimer && <p className="dealer-disclaimer">{dealerDisclaimer}</p>}
      <h1 className="seller-description">{t('vdp.sellerDescription')}</h1>
      <Truncate
        lines={isTruncated && collapsedLinesToShow}
        onTruncate={(truncatedText): void => {
          // when the text is over the truncate limit but we expand via the button do nothing
          if (truncatedText === false && isTextOverTruncateLimit === true) {
            return;
          }
          setIsTextOVerTruncateLimit(truncatedText);
        }}
      >
        {description}
      </Truncate>
      <Spacer size="16px" smSize="24px" />
      {isTextOverTruncateLimit &&
        (smsFunctionality && isMobile ? (
          <SmsVariantShowButton
            isTruncated={isTruncated}
            expand={expand}
            trackingParams={trackingParams}
          />
        ) : (
          <LinkButton onClick={handleExpandClick} className="link-button">
            {isTruncated ? t('vdp.showMore') : t('vdp.showLess')}
          </LinkButton>
        ))}
      <style jsx>{`
        .dealer-disclaimer {
          padding: 14px 16px;
          background: ${colors.G1};
          border-radius: 8px;
        }

        @media (min-width: ${screen.smMin}) {
          .dealer-disclaimer {
            padding: 18px 20px;
            border: solid 1px ${colors.G2};
          }
        }

        .description {
          display: flex;
          flex-direction: column;
          width: 100%;

          /* Normal text (16) */
          font-family: ${fonts.default};
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          line-height: 24px;

          /* Black */
          color: ${colors.BLACK};

          /* Fixes truncation issue */
          letter-spacing: 0;
          margin-top: 16px;
        }

        @media (min-width: ${screen.smMin}) {
          .description {
            margin-top: 16px;
          }
        }

        .seller-description {
          letter-spacing: 0.2px;
          text-transform: uppercase;
          font-weight: bold;
          font-size: 18px;
          line-height: 24px;
          margin-top: 10px;
          margin-bottom: 10px;
          display: none;
        }

        @media (min-width: ${screen.smMin}) {
          .seller-description {
            display: block;
          }
        }
      `}</style>
      {linkButtonCss.styles}
    </div>
  );
}
